import React, { useState, useMemo } from "react";
import NavBar from "./NavBar";
import NavBarMobile from "./NavBarMobile";
import { useMediaQuery } from "@uidotdev/usehooks";
import Form from "./Form";
import Footer from "./Footer";

import imageSlika1 from "../catalog/ledena-kisa-slika1-uvod.jpg";
import imageSlika2 from "../catalog/ledena-kisa-slika2-klimatologija.jpg";
import imageSlika3 from "../catalog/ledena-kisa-slika3-trend-1949-1985.jpg";
import imageSlika4 from "../catalog/ledena-kisa-slika4-trend-1991-2022.jpg";
import imageSlika5 from "../catalog/ledena-kisa-slika5-trajanje.jpg";
import imageSlika6 from "../catalog/ledena-kisa-slika6-pocetak-kraj.jpg";
import imageSlika7 from "../catalog/ledena-kisa-slika7-profili.jpg";
import imageSlika8 from "../catalog/ledena-kisa-slika8-ruze-vetra.jpg";

import {
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
} from "@material-tailwind/react";
import ReactHtmlParser from "html-react-parser";

/*
  FOLDERI SA SLIKAMA ZA TAB "PODACI" (src/):
  - src/slikeLedenaKisaGodine/   -> po jedna mapa za svaku godinu 1991-2020
                                    npr. ledena_kisa_1991.png (bitno je da u imenu postoji 4-cifrena godina)
  - src/slikeLedenaKisaSondaze/  -> po jedna sondaža za svaki datum 1974-2020
                                    npr. sondaza_1974-01-05.png (datum u imenu, YYYY-MM-DD ili YYYYMMDD)
  Padajući meniji se pune automatski na osnovu imena fajlova iz foldera.
*/

const figure = (src, caption, alt = "Ledena kiša") =>
  `<figure style="margin-bottom: 16px;">
     <img src="${src}" alt="${alt}" style="max-width:100%;height:auto;margin:0 auto;">
     <figcaption style="color: #000; font-size: 14px; text-align: left; margin-top: 6px;">${caption}</figcaption>
   </figure>`;

export default function LedeneKise() {
  const isSmallDevice = useMediaQuery("only screen and (max-width : 768px)");
  const isTabletOrLarger = useMediaQuery("only screen and (min-width: 769px)");
  const [activeTab, setActiveTab] = useState("uvod");
  const [activePodaciTab, setActivePodaciTab] = useState("broj-dana");

  // ---- Učitavanje slika iz foldera (lista se pravi automatski) ----
  const imagesGodine = useMemo(
    () =>
      require.context("../slikeLedenaKisaGodine", false, /\.(png|jpe?g|svg)$/),
    [],
  );
  const imagesSondaze = useMemo(
    () =>
      require.context("../slikeLedenaKisaSondaze", false, /\.(png|jpe?g|svg)$/),
    [],
  );

  const godine = useMemo(
    () =>
      imagesGodine
        .keys()
        .map((key) => {
          const match = key.match(/(\d{4})/);
          return match ? { key, label: match[1] } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [imagesGodine],
  );

  const MESECI = {
    jan: "01",
    feb: "02",
    mar: "03",
    apr: "04",
    may: "05",
    jun: "06",
    jul: "07",
    aug: "08",
    sep: "09",
    oct: "10",
    nov: "11",
    dec: "12",
  };

  const sondaze = useMemo(
    () =>
      imagesSondaze
        .keys()
        .map((key) => {
          const match = key.match(/(\d{1,2})([a-z]{3})(\d{4})/i);
          if (!match) return null;
          const d = match[1].padStart(2, "0");
          const m = MESECI[match[2].toLowerCase()];
          const y = match[3];
          if (!m) return null;
          return { key, label: `${d}.${m}.${y}`, sortKey: `${y}${m}${d}` };
        })
        .filter(Boolean)
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey)),
    [imagesSondaze],
  );

  const [godina, setGodina] = useState("");
  const [sondaza, setSondaza] = useState("");

  const selectedGodinaKey = godina || (godine[0] && godine[0].key) || "";
  const selectedSondazaKey = sondaza || (sondaze[0] && sondaze[0].key) || "";

  const godinaLabel =
    (godine.find((g) => g.key === selectedGodinaKey) || {}).label || "";
  const sondazaLabel =
    (sondaze.find((s) => s.key === selectedSondazaKey) || {}).label || "";

  const godinaImage = selectedGodinaKey ? imagesGodine(selectedGodinaKey) : "";
  const sondazaImage = selectedSondazaKey
    ? imagesSondaze(selectedSondazaKey)
    : "";

  const data = [
    {
      label: "UVOD",
      value: "uvod",
      desc:
        `<h2>UVOD</h2>
      <p style="margin-bottom: 10px; color: #000;">
        Ledene padavine se obično dele u tri tipa: ledenu kišu, ledenu rosulju i ledenu maglu. Ova pojava se dešava kada kišne kapi i/ili kapi rosulje se istovremeno lede pri dodiru sa predmetima na površini podloge, kao što su asfalt, beton, ali i dalekovodi i grane drveća koji imaju temperaturu ispod 0 °C. Ovim se stvara tanak sloj leda na njihovim površinama. Ledena kiša se javlja kada ledeni hidrometeori (snežne pahuljice, susnežica i/ili ledena zrnca) pri svom padu kroz atmosferu nailaze na topliji sloj vazduha, poznat i kao „topli nos“, koji leži iznad prizemnog sloja hladnog vazduha. Dati sloj je dovoljno debeo da u njemu se ledeni elementi u potpunosti tome u kapljice vode. Ove kapljice tada padaju kroz hladan sloj vazduha ispod postajući prehlađene kapi vode a koje se momentalno mrznu pri dodiru sa hladnim površinama (Slika 1). Javljanje ove pojave ekstremnog i opasnog vremena povezano je sa postojanjem temperaturne inverzije u najnižim slojevima atmosfere i prisustva pomenutog sloja toplog vazduha na visini, iako postoje i pojave ledene kiše bez prisustva „toplog nosa“ iznad podloge. Tada je to često u vezi sa vantropskim ciklonima i obližnjih vodenih površi i reljefa.
      </p>` +
        `<p style="color: #000;">
        Ovaj tip opasnog vremena, iako redak, jedan je od najopasnijih meteoroloških pojava. Ledena kiša može dovesti do prirodnih katastrofa u različitim delovima sveta i uticati na ljude, saobraćaj i imovinu. Pojavom ledene kiše može doći do nakupljanja leda na drveću i veštačkim objektima, dovodeći do pada dalekovoda i faznih provodnika, otežavajući uslove u saobraćaju, oštećenje infrastrukture, gubitak imovine, povrede ljudi na ravnim i popločanim površima. Znanje stečeno istraživanjem ledenih padavina može pružiti strategije za smanjenje rizika i poboljšanje otpornosti zajednica pogođenih zbog ovih pojava. To može dovesti do razvoja strategija za upravljanje katastrofama i poboljšanja bezbednosti i blagostanja pogođenih zajednica.
      </p>`,
      image: figure(
        imageSlika1,
        "<b>Slika 1</b> Šematski prikaz tipičnog vertikalnog profila temperature (crvena linija) za vreme pojave ledene kiše na tlu.",
        "Ledena kiša - uvod",
      ),
      desc2: `<p></p>`,
    },
    {
      label: "KLIMATOLOGIJA",
      value: "klimatologija",
      desc:
        `<h2>KLIMATOLOGIJA LEDENE KIŠE U SRBIJI</h2>
      <p style="margin-bottom: 10px; color: #000;">
        Na Slici 2 se može videti raspodela srednjeg godišnjeg broja dana sa ledenom kišom na teritoriji Srbije u poslednjem klimatološkom referentnom periodu (1991–2020) na osnovu standardnih osmatranja meteorološke mreže 28 glavnih meteoroloških stanica u Srbiji. Može se videti da istočnu Srbiju karakteriše najveći godišnji broj ovih dana (maksimum je približno 5,4 dana godišnje za stanicu „Crni vrh“). To je područje Karpata u Srbiji. Štaviše, studija (Kämäräinen et al. 2017) pokazuje da područje blizu Karpata ima najveću učestalost ledene kiše u Evropi. U Beogradu je, s druge strane, prosečan godišnji broj dana sa ledenom kišom tokom navedenog standardnog klimatološkog perioda nešto niži (3,6 dana godišnje). Treba napomenuti da je u većem delu Srbije zabeleženo manje od 2 dana ledene kiše godišnje u poslednjem tridesetogodišnjem klimatološkom referentnom periodu.
      </p>` +
        `<p style="color: #000;">
        Kämäräinen M, Hyvärinen O, Jylhä K, Vajda A, Neiglick S, Nuottokari J, Gregow H (2017) A method to estimate freezing rain climatology from ERA-Interim reanalysis over Europe. Nat Hazards Earth Syst Sci, 17:243–259.
      </p>`,
      image: figure(
        imageSlika2,
        "<b>Slika 2</b> Raspodela srednjeg godišnjeg broja dana sa ledenom kišom iznad Srbije za poslednji referentni klimatološki period (1991–2020).",
        "Ledena kiša - klimatologija",
      ),
      desc2: `<p></p>`,
    },
    {
      label: "TRENDOVI I TRAJANJE",
      value: "trendovi-i-trajanje",
      desc:
        `<h2>TRENDOVI I TRAJANJE</h2>
      <p style="margin-bottom: 10px; color: #000;">
        Analiza trenda broja dana sa pojavom ledene kiše u Beogradu vršena je u periodu 1949–2022. Podaci nisu dostupni za period 1986–1990. Važno je napomenuti da je linija linearne regresije za prvi period (1949–1985) opadajuća funkcija sa veoma niskom stopom od 0,0303 dana godišnje i niskom stopom značajnosti (R2 = 0,026) (Slika 3). Drugi period (1991–2022) karakteriše kontinuirani niz podataka koji je duži od klimatološkog minimuma (≥ 30). Godišnji broj dana sa ledenom kišom smanjivao se stopom od 0,1347 dana godišnje u periodu 1991–2022 (jednačina linije trenda je: y = –0,1347⸱x + 273,71; Slika 4). Navedeni trend je statistički značajan (α = 0,02984). Pored toga, koeficijent determinacije (R2) je 0,26. Ukupan broj dana godišnje tokom ovog perioda ne prelazi 9. Vremenom se može primetiti smanjenje broja dana sa ledenom kišom, što se može pripisati globalnom zagrevanju.
      </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
        <i>Slika 5</i> prikazuje raspodelu trajanja događaja ledene kiše u Beogradu za sve sezone u kojima se javlja ova pojava (crni stubići) i samo za zimu (sivi stubići) za period 1949–1985. može se videti da je najveći broj događaja ledene kiše kratkoživeći; oko 40% traje manje od 1 h, dok oko 62% traje kraće od 2 h. frekvencija trajanja ledene kiše naglo opada za duža vremena trajanja. Samo oko 1,6% analiziranih događaja traje duže od 10 h. Samo tokom zime, trajanje ledene kiše je slično (oko 39% traje kraće od 1 h i oko 61% traje ispod 2 h).
      </p>` +
        `<p style="color: #000;">
        Na Slici 6 vidi se dnevna raspodela početaka (crni stubići) i završetaka (sivi stubići) pojave ledene kiše za isti period 1949–1985. Ledena kiša najčešće počinje tokom noći i ranog jutra, u oko 48% slučajeva, kada je verovatnije prisustvo temperaturne inverzije i prizemne temperature vazduha ispod 0 °C. U datom delu dana, izdvajaju se maksimumi između 00 i 01 h (15,8%) i između 06 i 07 h (11,1%) po lokalnom vremenu. Visoka frekvencija početka analizirane pojave se primećuje i tokom popodneva, verovatno usled prolazaka toplih frontova ili tokom ledenih dana. Kraj pojave ledene kiše je približno ravnomerno raspodeljen tokom dana. Primećuje se maksimum između 04 i 05 h po lokalnom vremenu (oko 11%) i nekoliko maksimuma tokom dana.
      </p>`,
      image:
        `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:16px;">` +
        figure(
          imageSlika3,
          "<b>Slika 3</b> Ukupan godišnji broj dana sa ledenom kišom u Beogradu u periodu 1949–1985. Crvena debela linija predstavlja linearnu regresiju.",
          "Slika 3",
        ) +
        figure(
          imageSlika4,
          "<b>Slika 4</b> Isto kao Slika 3 samo u periodu 1991–2022.",
          "Slika 4",
        ) +
        figure(
          imageSlika5,
          "<b>Slika 5</b> Trajanje (u časovima) događaja ledene kiše (crni stubići) i specijalno za zimu (sivi stubići) u periodu 1949–1985.",
          "Slika 5",
        ) +
        figure(
          imageSlika6,
          "<b>Slika 6</b> Frekvencija početka (crni stubići) i kraja (sivi stubići) pojave ledene kiše u Beogradu po satnim intervalima (UTC+1) tokom dana za period 1949–1985.",
          "Slika 6",
        ) +
        `</div>`,
      desc2: `<p></p>`,
    },
    {
      label: "VERTIKALNA STRUKTURA",
      value: "vertikalna-struktura",
      desc:
        `<h2>VERTIKALNA STRUKTURA ATMOSFERE</h2>
      <p style="margin-bottom: 10px; color: #000;">
        Na Slici 7 prikazani su vertikalni profili temperature vazduha u najnižih 2 km visine, dobijeni iz radiosonažnih merenja u Beogradu u danima sa pojavom ledene kiše i to za termin 00 UTC u periodu 1974–2022. Karakteristični vertikalni profili temperature vazduha pri pojavi ledene kiše obično uključuju inverziju temperature u najnižem sloju neposredno uz tlo kao i pojavu tzv. „toplog nosa“ koju karakterišu temperature iznad 0 °C blizu 850 hPa (oko 1,5 km visine) i potom opadanje temperature iznad. Srednje sondaže za novembar i decembar pokazuju karakterističan vertikalni profil temperature sa „toplim nosem“ iznad prehlađenog sloja prizemnog vazduha (t < 0 °C). Srednje sondaže za ostale mesece ne pokazuju ovaj karakterističan vertikalni profil temperature što je posledica osrednjanja većeg broja sondaža bez „toplog nosa“. Takođe, imaju relativno slabe prehlađene površinske slojeve (do –3 °C ), izuzev za februar. Svi vertikalni profili pokazuju jednu ili više temperaturnih inverzija. U slučaju postojanja „toplog nosa“, ledeni padavinski elementi (snežne pahuljice, ledena zrnca i susnežica) padaju kroz ovaj relativno topao sloj vazduha, gde se tope, te padaju ka tlu postajuću prehlađene kapi koje se momentalno mrznu u dodiru sa predmetima na tlu formirajući tanak sloj leda na njihovim površinama. Od ukupno 96 analiziranih sondaža njih 58 (60,4%) pokazuju vertikalni profil sa „toplim nosem“. To je u saglasnosti sa studijom (Carrière et al. 2000) koja ističe da 30–40% sondaža nema ovu karakteristiku na visini.
      </p>` +
        `<p style="color: #000;">
        Carrière, J.-M., Lainard, C., Le Bot, C., Robart, F. 2000. A climatological study of surface freezing precipitation in Europe, Meteorol. Appl., 7:229–238.
      </p>`,
      image: figure(
        imageSlika7,
        "<b>Slika 7</b> Osrednjeni vertikalni profili temperature vazduha po mesecima za stanicu „Beograd-Košutnjak“ u 00 UTC za vreme događaja ledene kiše u periodu 1974–2022.",
        "Vertikalna struktura atmosfere",
      ),
      desc2: `<p></p>`,
    },
    {
      label: "LEDENA KIŠA I KOŠAVA",
      value: "ledena-kisa-i-kosava",
      desc:
        `<h2>LEDENA KIŠA I KOŠAVA</h2>
      <p style="margin-bottom: 10px; color: #000;">
        Potvrđeno je da postoji snažna veza između pojave ledene kiše i košave (Kovačević i sar., 2025) analizirajući srednje sinoptičke karte u prizemlju i na visini za one dane kada je bilo javljanje date pojave ekstremnog vremena (nije prikazano). Pokazano je da se ledena kiša često javlja kada se blokirajući anticikloni razvijaju istočno od Srbije a cikloni se formiraju iznad Apeninskog poluostrva i Zapadnog Sredozemlja. Ovo su tipične sinoptičke situacije pri kojima se takođe javlja košava, kao snažan lokalni vetar iz istočnog i jugoistočnog pravca pri približno paralelnim i skoro meridionalno pružajućim izobarama iznad Srbije. Na Slici 8 tu potvrdu možemo videti i analizirajući ruže vetrova po brzini (a, b) i čestini (c, d) za stanicu „Beograd-Vračar“ za termine 07 h (a, c) i 21 h (b, d) po lokalnom vremenu. Uzeta su baš ova dva klimatološka termina pošto se ledena kiša najčešće javlja tokom noći i rano ujutru, ali i zato što košava svoj maksimum obično ima između 05 i 10 h po lokalnom vremenu. Može se primetiti da jugoistočni (SE), istok-jugoistočni (ESE) i jug-jugoistočni (SSE) preovlađuju tokom onih dana kada je zabeležena pojava ledene kiše u Beogradu. Ukupan udeo vetrova iz ovih pravaca je oko 43%, odnosno, oko 46%, za 07 i 21 h redom. Takođe, ESE, SE i SSE vetrovi imaju veće intenzitete brzina (a, b) aludirajući na košavu koja je vetar iz tipično jugoistočnog pravca i okarakterisana većim intenzitetom brzine.
      </p>` +
        `<p style="color: #000;">
        Kovačević, N., Koračin, K.V., Putniković, S. 2025. Climatological study of freezing rain in Belgrade from 1949 to 2022, Theor. Appl. Climatol., 156, 200.
      </p>`,
      image: figure(
        imageSlika8,
        "<b>Slika 8</b> Ruže vetra za intenzitet brzine vetra (a, b) i čestine vetra (c, d) za 07 h (a, c) i 21 h (b, d) po lokalnom vremenu za dane sa ledenom kišom u Beogradu.",
        "Ledena kiša i košava",
      ),
      desc2: `<p></p>`,
    },
    {
      label: "PODACI",
      value: "podaci",
      tabs: [
        {
          label: "Broj dana po godini",
          value: "broj-dana",
          content: (
            <div
              className={`suseDeo ${
                isTabletOrLarger ? "flex" : "flex flex-col"
              }`}
            >
              <div className="suseDeoVeci w-full md:w-2/3 p-4">
                <p className="mb-4">
                  Izaberite godinu u poslednjem referentnom klimatološkom
                  periodu (1991–2020).
                </p>

                <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-4">
                  <label className="font-semibold">Izaberi godinu:</label>
                  <select
                    value={selectedGodinaKey}
                    onChange={(e) => setGodina(e.target.value)}
                    className="border p-2 w-full md:w-auto"
                  >
                    {godine.map(({ key, label }) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                {godinaImage && (
                  <div className="mt-6 w-full md:w-auto">
                    <h4 className="text-lg font-semibold">
                      Broj dana sa ledenom kišom, {godinaLabel}
                    </h4>
                    <img
                      src={godinaImage}
                      alt={`Broj dana sa ledenom kišom, ${godinaLabel}`}
                      className="w-full md:w-auto max-w-md mx-auto"
                    />
                  </div>
                )}

                <div className="mt-6 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
                  <a
                    href={godinaImage}
                    download={`Ledena-kisa-${godinaLabel}.jpg`}
                    className="bg-blue-500 font-bold text-white px-4 py-2 rounded text-center"
                  >
                    Preuzmi sliku
                  </a>
                </div>
              </div>

              <div className="suseDeoManji w-full md:w-1/3 p-4 bg-white border-l">
                <h4 className="text-lg font-semibold">
                  BROJ DANA SA LEDENOM KIŠOM PO GODINI
                </h4>
                <p className="mt-4 text-black">
                  Ovde možete preuzeti broj dana sa ledenom kišom po godini u
                  poslednjem referentnom klimatološkom periodu (1991–2020).
                </p>
              </div>
            </div>
          ),
        },
        {
          label: "Vertikalne sondaže",
          value: "vertikalne-sondaze",
          content: (
            <div
              className={`suseDeo ${
                isTabletOrLarger ? "flex" : "flex flex-col"
              }`}
            >
              <div className="suseDeoVeci w-full md:w-2/3 p-4">
                <p className="mb-4">
                  Izaberite datume u periodu 1974–2020 za one dane sa ledenom
                  kišom u Beogradu da biste dobili vertikalnu sondažu atmosfere.
                </p>

                <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-4">
                  <label className="font-semibold">Izaberi datum:</label>
                  <select
                    value={selectedSondazaKey}
                    onChange={(e) => setSondaza(e.target.value)}
                    className="border p-2 w-full md:w-auto"
                  >
                    {sondaze.map(({ key, label }) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                {sondazaImage && (
                  <div className="mt-6 w-full md:w-auto">
                    <h4 className="text-lg font-semibold">
                      Vertikalna sondaža atmosfere, {sondazaLabel}
                    </h4>
                    <img
                      src={sondazaImage}
                      alt={`Vertikalna sondaža atmosfere, ${sondazaLabel}`}
                      className="w-full md:w-auto max-w-md mx-auto"
                    />
                  </div>
                )}

                <div className="mt-6 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
                  <a
                    href={sondazaImage}
                    download={`Sondaza-${sondazaLabel}.png`}
                    className="bg-blue-500 font-bold text-white px-4 py-2 rounded text-center"
                  >
                    Preuzmi sliku
                  </a>
                </div>
              </div>

              <div className="suseDeoManji w-full md:w-1/3 p-4 bg-white border-l">
                <h4 className="text-lg font-semibold">
                  VERTIKALNE SONDAŽE ATMOSFERE
                </h4>
                <p className="mt-4 text-black">
                  Ovde možete preuzeti vertikalne sondaže atmosfere u danima sa
                  ledenom kišom u Beogradu od 1974. do 2020. godine.
                </p>
              </div>
            </div>
          ),
        },
      ],
    },
  ];

  return (
    <>
      <style>{`
        .ledenaKisaStranica .suseDeoManji h2 {
          font-size: clamp(1.1rem, 1.4vw, 1.35rem) !important;
          line-height: 1.4 !important;
          font-weight: 700 !important;
          margin: 0 0 0.75rem 0 !important;
          text-align: left !important;
        }
        .ledenaKisaStranica .suseDeoManji p {
          font-size: clamp(0.9rem, 1vw, 1rem) !important;
          line-height: 1.65 !important;
          text-align: justify;
        }
        .ledenaKisaStranica .suseDeo h4 {
          font-size: clamp(0.95rem, 1.1vw, 1.1rem) !important;
          line-height: 1.4 !important;
        }

        /* Glavna dugmad: isti izgled kao na ostalim stranicama */
        @media (min-width: 769px) {
          .ledenaKisaStranica .tabsGlavni {
            gap: 12px !important;
            flex-wrap: wrap !important;
            justify-content: center !important;
          }
          .ledenaKisaStranica .tabsGlavni .probaTaba {
            flex: 1 1 auto !important;
            width: auto !important;
            min-width: 120px;
            padding: 10px 18px !important;
            height: auto !important;
            border: 1px solid #000 !important;
            border-radius: 10px !important;
            background: transparent !important;
          }
          .ledenaKisaStranica .tabsGlavni .probaTaba span {
            font-size: clamp(0.95rem, 1.25vw, 1.2rem) !important;
            font-weight: 700 !important;
            line-height: 1.25 !important;
            color: #000 !important;
            white-space: nowrap;
          }
        }
        @media (max-width: 768px) {
          .ledenaKisaStranica .suseDeoManji h2 {
            font-size: 1.15rem !important;
          }
        }
      `}</style>

      {isTabletOrLarger && <NavBar />}
      {isSmallDevice && <NavBarMobile />}
      <div className="ledenaKisaStranica container mx-auto text-center min-h-screen md:mb-6">
        <Tabs value={activeTab} className="max-w-screen-xl mx-auto">
          <TabsHeader
            className="tabsGlavni bg-transparent flex justify-center flex-wrap md:flex-nowrap gap-2 mx-2 md:mx-0"
            indicatorProps={{
              className: "bg-gray-900/10 shadow-none !text-gray-900",
            }}
            onChange={(value) => setActiveTab(value)}
          >
            {data.map(({ label, value }) => (
              <Tab className="probaTaba" key={value} value={value}>
                <span style={{ fontWeight: 700, lineHeight: 1.25 }}>
                  {label}
                </span>
              </Tab>
            ))}
          </TabsHeader>
          <TabsBody>
            {data.map(({ value, tabs, desc, image, desc2 }) => (
              <TabPanel key={value} value={value}>
                {value === "podaci" ? (
                  <Tabs
                    value={activePodaciTab}
                    onChange={(val) => setActivePodaciTab(val)}
                  >
                    <TabsHeader className="bg-gray-200">
                      {tabs.map(({ label, value }) => (
                        <Tab key={value} value={value}>
                          <span style={{ fontSize: "0.9rem", fontWeight: 600 }}>
                            {label}
                          </span>
                        </Tab>
                      ))}
                    </TabsHeader>
                    <TabsBody>
                      {tabs.map(({ value, content }) => (
                        <TabPanel key={value} value={value}>
                          {content}
                        </TabPanel>
                      ))}
                    </TabsBody>
                  </Tabs>
                ) : (
                  <>
                    <div className="suseDeo flex flex-col md:flex-row">
                      <div className="suseDeoVeci w-full md:w-3/5 p-4">
                        {image && ReactHtmlParser(image)}
                        {desc2 && ReactHtmlParser(desc2)}
                      </div>
                      <div className="suseDeoManji w-full md:w-2/5 p-4 bg-white mt-6 md:mt-0">
                        {desc && ReactHtmlParser(desc)}
                      </div>
                    </div>
                  </>
                )}
              </TabPanel>
            ))}
          </TabsBody>
        </Tabs>
      </div>
      <Form />
      <Footer />
    </>
  );
}
